import 'chartjs-adapter-luxon';

import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
);

const WINDOW_MS = 60_000;

const CHART_OPTIONS = {
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'time',
      time: { unit: 'second', displayFormats: { second: 'HH:mm:ss' } },
      ticks: { maxTicksLimit: 6, color: '#5f6b7a' },
      grid: { color: '#e9ebed' },
    },
    y: {
      ticks: { color: '#5f6b7a' },
      grid: { color: '#e9ebed' },
    },
  },
  plugins: {
    legend: { position: 'top', labels: { color: '#16191f', boxWidth: 12 } },
    tooltip: { mode: 'index', intersect: false },
  },
};

function buildDatasets(showVessel, seriesRef) {
  const datasets = [];
  if (showVessel) {
    datasets.push({
      label: 'Vessel pressure (bar)',
      data: seriesRef.current.vesselPressure,
      borderColor: '#ce3b26',
      backgroundColor: 'transparent',
      pointRadius: 0,
      tension: 0.2,
    });
  }
  datasets.push({
    label: 'Pipette tip pressure (bar)',
    data: seriesRef.current.pipetteTipPressure,
    borderColor: '#0972d3',
    backgroundColor: 'transparent',
    pointRadius: 0,
    tension: 0.2,
  });
  return datasets;
}

export default function ChannelChart({
  vesselPressure, pipetteTipPressure, showVessel,
}) {
  const seriesRef = useRef({ vesselPressure: [], pipetteTipPressure: [] });
  const chartRef = useRef(null);

  useEffect(() => {
    const now = Date.now();
    const cutoff = now - WINDOW_MS;
    const push = (arr, v) => {
      arr.push({ x: now, y: typeof v === 'number' ? v : null });
      while (arr.length > 0 && arr[0].x < cutoff) arr.shift();
    };
    push(seriesRef.current.vesselPressure, vesselPressure);
    push(seriesRef.current.pipetteTipPressure, pipetteTipPressure);

    if (chartRef.current) {
      chartRef.current.options.scales.x.min = cutoff;
      chartRef.current.options.scales.x.max = now;
      chartRef.current.data.datasets = buildDatasets(showVessel, seriesRef);
      chartRef.current.update('none');
    }
  }, [pipetteTipPressure, showVessel, vesselPressure]);

  const data = { datasets: buildDatasets(showVessel, seriesRef) };

  return (
    <div style={{ height: 200 }}>
      <Line ref={chartRef} data={data} options={CHART_OPTIONS} />
    </div>
  );
}

ChannelChart.propTypes = {
  vesselPressure: PropTypes.number.isRequired,
  pipetteTipPressure: PropTypes.number.isRequired,
  showVessel: PropTypes.bool.isRequired,
};
